import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { ResponsiveImage, VideoPlayer, AspectRatioWrapper } from '../../components';
import classNames from 'classnames';

import css from './ListingPage.module.css';

const SectionGalleryMaybe = props => {
  const { intl, images, videos, currentStock, isMobileLayout, imageVariants, handleViewPhotosClick } = props;

  // Combine images and videos into a single media array
  const allMedia = [
    ...(images || []).map((img, index) => ({
      type: 'image',
      data: img,
      id: `image-${index}`,
    })),
    ...(videos || []).map((video, index) => ({
      type: 'video',
      data: video,
      id: `video-${index}`,
    })),
  ];

  // Don't render if no media
  if (!allMedia || allMedia.length === 0) {
    return null;
  }

  // Split media into featured (first 3) and additional (next 4)
  const featuredMedia = allMedia.slice(0, 3);
  const additionalMedia = allMedia.slice(3, 7); // Show 4 items in second row
  const hasMoreMedia = allMedia.length > 7;
  const remainingCount = allMedia.length - 7;

  const handleImageClick = (e, index) => {
    // Stop event from bubbling up
    e.stopPropagation();
    
    // Convert media index to image index (since ImageCarousel only shows images)
    const mediaItem = allMedia[index];
    if (mediaItem && mediaItem.type === 'image') {
      // Find the index of this image in the images array
      const imageIndex = (images || []).findIndex(img => img.id.uuid === mediaItem.data.id.uuid);
      handleViewPhotosClick(e, imageIndex >= 0 ? imageIndex : 0);
    } else {
      // If it's a video, just open carousel with first image
      handleViewPhotosClick(e, 0);
    }
  };

  const renderMediaItem = (item, index, size = 'large', showOverlay = false) => {
    const isVideo = item.type === 'video';
    const mediaClassName = classNames(
      css.galleryItem,
      size === 'large' ? css.galleryItemLarge : css.galleryItemSmall,
      { [css.galleryItemVideo]: isVideo }
    );

    // Set aspect ratios based on size - large items are 4:3 (taller to match bottom row), small items are 1:1
    const aspectWidth = size === 'large' ? 4 : 1;
    const aspectHeight = size === 'large' ? 3 : 1;

    if (isVideo) {
      return (
        <div key={item.id} className={mediaClassName}>
          <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
            <VideoPlayer
              video={item.data}
              className={css.galleryVideoPlayer}
              settings={{ playerTime: false, hoverPlay: false }}
            />
          </AspectRatioWrapper>
        </div>
      );
    }

    return (
      <div key={item.id} className={mediaClassName} onClick={(e) => handleImageClick(e, index)}>
        <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
          <ResponsiveImage
            rootClassName={css.galleryImage}
            alt={intl.formatMessage({ id: 'SectionGallery.imageAltText' })}
            image={item.data}
            variants={imageVariants}
          />
          {showOverlay && (
            <div className={css.galleryItemOverlay}>
              <div className={css.showMoreOverlayContent}>
                +{remainingCount}
              </div>
            </div>
          )}
        </AspectRatioWrapper>
      </div>
    );
  };

  const sectionTitle = intl.formatMessage(
    { id: 'SectionGallery.title' },
    { count: allMedia.length }
  );

  return (
    <div className={css.sectionGallery}>
      <h2 className={css.galleryTitle}>{sectionTitle}</h2>

      <div className={css.galleryGrid}>
        {/* Featured items row - 3 large items */}
        <div className={css.featuredRow}>
          {featuredMedia.map((item, index) => renderMediaItem(item, index, 'large'))}
        </div>

        {/* Additional items row - 4 actual images */}
        {additionalMedia.length > 0 && (
          <div className={css.additionalRow}>
            {additionalMedia.map((item, index) => {
              // Show overlay on the last item if there are more media
              const isLastItem = index === additionalMedia.length - 1;
              const shouldShowOverlay = isLastItem && hasMoreMedia;
              return renderMediaItem(item, index + 3, 'small', shouldShowOverlay);
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionGalleryMaybe;
